import {Component, Input, OnInit} from '@angular/core';
import {
    ContactDetailsApiService,
    ContactType,
    IncidentContactDetailsResponse,
    IncidentResponse,
    MsisdnStatus,
    ReferenceBookResponse,
    ReferenceBookType,
} from '@npck/af-frontend-api';
import {DATE_TIME_SERVER_FORMAT, getFormattedDateOrNull, NESTED_TAB, RouterService, rxSpinner} from '@npck/libs/commons';
import {DialogService} from '@npck/libs/ui-kit';
import {combineLatest, filter, mergeMap} from 'rxjs';
import {fieldExtractor, FilterDictionaryField, FilterField} from '../../../../components/full-table/filter/filter.component';
import {initialTableEvent, mergeTableEvent, TableEvent} from '../../../../components/full-table/full-table.component';
import {Column, ColumnType, CustomLayoutColumn, Row} from '../../../../components/full-table/table/table.component';
import {mapMsisdnStatusToStyle} from '../../../../components/status/status.component';
import {AuthHolderService} from '../../../../domain/auth-holder.service';
import {DictionaryHolderService, findValueInDictionary, resolveRefName} from '../../../../domain/dictionary-holder.service';
import {IncidentDetailsFilterHolderService} from '../../../../domain/incident-details-filter-holder.service';
import {formatMsisdnDisplay} from '../../../../domain/phone-utils';
import {CONTACT_DETAILS_ID, INCIDENT_ID} from '../../../../domain/query-params.keys';
import {Path} from '../../../../path';

@Component({
    selector: 'af-contacts-tab',
    standalone: false,
    templateUrl: './contacts-tab.component.html',
})
export class ContactsTabComponent implements OnInit {
    @Input() incident: IncidentResponse;
    hasDeleteAccess = false;
    filterFields: FilterField[] = [
        {
            type: 'text',
            name: 'query',
        },
        <FilterDictionaryField>{
            isMultiSelect: true,
            type: 'select',
            name: 'contactTypes',
            dictionaryName: ReferenceBookType.CONTACT_TYPE,
            values: [],
        },
        <FilterDictionaryField>{
            isMultiSelect: true,
            type: 'select',
            name: 'msisdnStatuses',
            dictionaryName: ReferenceBookType.MSISDN_STATUS,
            values: [],
        },
        <FilterField>{
            type: 'dateRange',
            name: 'contactDateTimeRange',
            translationPrefix: 'contactDetails',
        },
        <FilterField>{
            type: 'dateRange',
            name: 'victimAppealDateTimeRange',
            translationPrefix: 'contactDetails',
        },
    ];
    dataSource: Row[] = [];
    tableEvent: TableEvent;
    totalElementsForFilter: number;
    totalElements: number;

    constructor(
        private contactDetailsApiService: ContactDetailsApiService,
        private dictionaryHolderService: DictionaryHolderService,
        private routerService: RouterService,
        private authHolderService: AuthHolderService,
        private dialogService: DialogService,
        private filterHolderService: IncidentDetailsFilterHolderService,
    ) {
    }

    ngOnInit(): void {
        this.hasDeleteAccess = this.authHolderService.hasAccess(['npck-admin']);
        this.loadContactDetails(
            this.filterHolderService.getState(this.incident.id, 'tabContacts')?.tableEvent
                ?? this.buildInitialTableEvent(),
        );
    }

    loadContactDetails(tableEvent: TableEvent): void {
        const prevEvent = this.filterHolderService.getState(this.incident.id, 'tabContacts')?.tableEvent;
        this.tableEvent = mergeTableEvent(prevEvent, tableEvent);
        this.filterHolderService.storeTableState(this.incident.id, 'tabContacts', this.tableEvent);
        combineLatest([
            this.dictionaryHolderService.getReference(ReferenceBookType.CONTACT_TYPE),
            this.dictionaryHolderService.getReference(ReferenceBookType.MSISDN_STATUS),
            this.contactDetailsApiService.getIncidentContactDetails(this.incident.id, {
                searchQuery: this.tableEvent.filter.query,
                limit: this.tableEvent.page.pageSize,
                offset: this.tableEvent.page.pageOffset,
                sortBy: this.tableEvent.sortBy?.columnName,
                sortOrder: this.tableEvent.sortBy?.direction,
                contactTypes: fieldExtractor(this.tableEvent.filter?.contactTypes) as ContactType[],
                statuses: fieldExtractor(this.tableEvent.filter?.msisdnStatuses) as MsisdnStatus[],
                contactDateFrom: getFormattedDateOrNull(
                    this.tableEvent.filter?.contactDateTimeRange?.startDate?.date,
                    DATE_TIME_SERVER_FORMAT,
                ),
                contactDateTo: getFormattedDateOrNull(
                    this.tableEvent.filter?.contactDateTimeRange?.endDate?.date,
                    DATE_TIME_SERVER_FORMAT,
                ),
                victimAppealDateFrom: getFormattedDateOrNull(
                    this.tableEvent.filter?.victimAppealDateTimeRange?.startDate?.date,
                    DATE_TIME_SERVER_FORMAT,
                ),
                victimAppealDateTo: getFormattedDateOrNull(
                    this.tableEvent.filter?.victimAppealDateTimeRange?.endDate?.date,
                    DATE_TIME_SERVER_FORMAT,
                ),
            }),
        ])
            .pipe(rxSpinner(this.tableEvent.spinnerId))
            .subscribe(([contactTypes, msisdnStatuses, response]) => {
                this.buildDataSource(response, contactTypes, msisdnStatuses);
            });
    }

    navigateToCreateContact(): void {
        this.navigateToContact(Path.INCIDENTS_CONTACT_DETAILS_CREATE);
    }

    deleteContact(contactDetailsId: string, contactTypeName: string): void {
        this.dialogService.openDialog({
            title: 'i18n_af_contactDetailsDeletionDialogTitle',
            subtitle: 'i18n_af_contactDetailsDeletionDialogSubtitle',
            buttonOkText: 'i18n_af_buttonDelete',
            buttonCancelText: 'i18n_af_buttonCancel',
            isButtonOkAccent: true,
            translationParams: {contactType: contactTypeName},
        })
            .pipe(
                filter(response => !!response),
                mergeMap(() => this.contactDetailsApiService.deleteContactDetailsById(this.incident.id, contactDetailsId)),
                rxSpinner('contactDetails'),
            )
            .subscribe(() => this.loadContactDetails(this.tableEvent));
    }

    private buildInitialTableEvent(): TableEvent {
        return {
            ...initialTableEvent,
            spinnerId: 'contactDetails',
            filter: {
                query: '',
            },
            sortBy: {
                columnName: 'contactDateTime',
                direction: 'asc',
            },
        };
    }

    private navigateToContact(path: string, contactDetailsId?: string): void {
        const pathParams: Record<string, string> = {[INCIDENT_ID]: this.incident.id};
        if (contactDetailsId) {
            pathParams[CONTACT_DETAILS_ID] = contactDetailsId;
        }
        this.routerService.navigate(path, {
            pathParams,
            queryParams: {[NESTED_TAB]: 'tabContacts'},
            queryParamsHandling: 'merge',
        });
    }

    private navigateToMsisdn(msisdnId: string): void {
        this.routerService.navigate(`${Path.SUBJECTS}/msisdn/${msisdnId}`);
    }

    private buildDataSource(
        response: IncidentContactDetailsResponse,
        contactTypes: ReferenceBookResponse[],
        msisdnStatuses: ReferenceBookResponse[],
    ): void {
        this.totalElementsForFilter = response.total;
        this.totalElements = response.total;
        this.dataSource = response.items.map(item => {
            return <Row>{
                click: () => this.navigateToContact(Path.INCIDENTS_CONTACT_DETAILS_DETAILS, item.id),
                ...(this.incident.isDeleted || this.incident.isInnocentStatus ? {} : {
                    button: <CustomLayoutColumn>{
                        type: ColumnType.CUSTOM_LAYOUT,
                        cellStyle: {
                            layoutDirection: 'row',
                            layoutAlignFirst: 'start',
                            layoutAlignSecond: 'center',
                            layoutGap: '8px',
                        },
                        layoutContentItems: [
                            {
                                type: 'icon',
                                text: 'ic_edit',
                                click: () => this.navigateToContact(Path.INCIDENTS_CONTACT_DETAILS_EDIT, item.id),
                                tooltip: 'i18n_af_labelEdit',
                                iconSize: 'i-16',
                            },
                            ...(this.hasDeleteAccess ? [{
                                type: 'icon',
                                text: 'ic_delete',
                                click: () => this.deleteContact(item.id,
                                    findValueInDictionary(contactTypes, item.contactType, 'name_ru') ?? item.contactType),
                                tooltip: 'i18n_af_labelDeactivate',
                                iconSize: 'i-16',
                                iconColor: 'c-secondary-red-800',
                            }] : []),
                        ],
                    },
                }),
                contactType: <Column>{
                    type: ColumnType.TEXT,
                    text: findValueInDictionary(contactTypes, item.contactType, 'name_ru') ?? item.contactType,
                },
                contactDateTime: <Column>{
                    type: ColumnType.DATE_TIME_SECONDS,
                    sortable: true,
                    text: item.contactDate,
                },
                msisdn: <Column>{
                    type: item.msisdnId ? ColumnType.LINK : ColumnType.TEXT,
                    text: formatMsisdnDisplay(item.msisdn),
                    click: item.msisdnId ? () => this.navigateToMsisdn(item.msisdnId) : undefined,
                },
                status: <Column>{
                    type: ColumnType.STATUS,
                    sortable: true,
                    text: item.status ? resolveRefName(msisdnStatuses, item.status) : null,
                    style: mapMsisdnStatusToStyle(item.status),
                },
                victimAppealDateTime: <Column>{
                    type: ColumnType.DATE_TIME_SECONDS,
                    sortable: true,
                    text: item.victimAppealDate,
                },
            };
        });
    }
}
